import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { startWith } from 'rxjs';
import {
  ESqlOperators,
  IMultiselectApiDropdownFilterSave,
  IShiftPhraseFilterSave,
  TFilterSave,
} from 'scw-digital-boards-widgets';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { IDropdownOption, MatDropdown } from '../../mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../mat-dropdown/scw-mat-dropdown.interface';
import { IAdvancedFilterApiValueConfig } from '../advanced-filter.interface';
import { AdvancedFilterService } from '../advanced-filter.service';

type TShiftFilterDialogMode = 'currentShift' | 'previousShift' | 'other';

/** Passed in only when the dialog is opened from the merged current/previous-shift chip's edit click — the
 * couple's shared operator/line, read directly off the chip (never re-derived from a sibling `lineId` filter). */
export interface IShiftFilterDialogData {
  mode: 'currentShift' | 'previousShift';
  lineId: number;
}

interface IShiftFilterDialogForm {
  mode: FormControl<TShiftFilterDialogMode>;
  lineId: FormControl<IDropdownOption[]>;
  shiftIds: FormControl<IDropdownOption[]>;
  includeDate: FormControl<boolean>;
  includeShift: FormControl<boolean>;
  includeLine: FormControl<boolean>;
}

@Component({
  imports: [
    MatButton,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatCheckbox,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatDropdown,
    MatIcon,
    MatIconButton,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  providers: [AdvancedFilterService],
  selector: 'app-shift-filter-dialog',
  styleUrl: './shift-filter-dialog.component.scss',
  templateUrl: './shift-filter-dialog.component.html',
})
export class ShiftFilterDialogComponent implements OnInit {
  public static dialogConfig: MatDialogConfig = {
    closeOnNavigation: true,
    disableClose: true,
    exitAnimationDuration: 0,
    restoreFocus: false,
    width: EDialogWidth.xSmall,
  };

  private readonly matDialogRef = inject(MatDialogRef<ShiftFilterDialogComponent, TFilterSave[]>);
  private readonly translate = inject(TranslateService);
  private readonly advancedFilterService = inject(AdvancedFilterService);
  private readonly dialogData = inject<IShiftFilterDialogData | undefined>(MAT_DIALOG_DATA, { optional: true });

  public readonly isEditMode = Boolean(this.dialogData);

  // Reuses the resources the old inline `shiftId` chip editor already had wired to the page's current filters
  // (site/line dependencies) via `AdvancedFilterManager` — not fresh ones, so behavior (and in-flight requests)
  // stays identical to what the replaced inline editor did.
  private readonly shiftFieldConfigurations = this.advancedFilterService.manager
    .configuration()
    .find(({ field }) => field === 'shiftId')?.configurations as IAdvancedFilterApiValueConfig[] | undefined;

  private readonly shiftResource = this.shiftFieldConfigurations?.find(({ matcherName }) => matcherName === 'shiftId')
    ?.resource;
  private readonly lineResource = this.shiftFieldConfigurations?.find(({ matcherName }) => matcherName === 'lineId')
    ?.resource;

  // `hasDelayedDefaultValue` (see `lineDropdownSettings` below) only replaces the placeholder `'...'` name once
  // `lineDropdownOptions()` *changes* after the placeholder was assigned — if real options were already present
  // on the very first read (e.g. this shared resource was already warm from elsewhere on the page), there's no
  // subsequent change to react to and the placeholder is never resolved. Gating on `ngOnInit` guarantees options
  // are still empty while `lineId`'s initial value is being assigned in the constructor (see
  // `getInitialLineIdValue`), so the later switch to real data always fires as a genuine change.
  private readonly allowLineOptions = signal(false);

  public readonly lineDropdownOptions = computed(() =>
    this.allowLineOptions() ? (this.lineResource?.formattedData() ?? []) : [],
  );
  public readonly shiftDropdownOptions = computed(() => this.shiftResource?.formattedData() ?? []);

  public readonly lineDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('advanceFilter.field.lineId'),
  };

  public readonly shiftDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: false,
    text: this.translate.instant('advanceFilter.field.shiftId'),
  };

  public readonly shiftFilterForm = new FormGroup<IShiftFilterDialogForm>({
    includeDate: new FormControl(true, { nonNullable: true }),
    includeLine: new FormControl(true, { nonNullable: true }),
    includeShift: new FormControl(true, { nonNullable: true }),
    lineId: new FormControl(this.getInitialLineIdValue(), { nonNullable: true, validators: [Validators.required] }),
    mode: new FormControl<TShiftFilterDialogMode>(this.dialogData?.mode ?? 'currentShift', { nonNullable: true }),
    shiftIds: new FormControl([], { nonNullable: true, validators: [Validators.required] }),
  });

  public readonly selectedMode = toSignal(this.shiftFilterForm.controls.mode.valueChanges, {
    initialValue: this.shiftFilterForm.controls.mode.value,
  });

  public readonly isOtherMode = computed(() => this.selectedMode() === 'other');
  public readonly isShiftPhraseMode = computed(
    () => this.selectedMode() === 'currentShift' || this.selectedMode() === 'previousShift',
  );

  private readonly includeDateSignal = toSignal(this.shiftFilterForm.controls.includeDate.valueChanges, {
    initialValue: this.shiftFilterForm.controls.includeDate.value,
  });
  private readonly includeShiftSignal = toSignal(this.shiftFilterForm.controls.includeShift.valueChanges, {
    initialValue: this.shiftFilterForm.controls.includeShift.value,
  });
  private readonly includeLineSignal = toSignal(this.shiftFilterForm.controls.includeLine.valueChanges, {
    initialValue: this.shiftFilterForm.controls.includeLine.value,
  });

  /** Date/Shift are the two phrase-resolved fields this dialog exists for; Line is just an extra convenience
   * filter alongside them, so it doesn't count toward "did this submission actually ask for anything". */
  public readonly isNoFilterToAddSelected = computed(
    () => this.isShiftPhraseMode() && !this.includeDateSignal() && !this.includeShiftSignal(),
  );

  public readonly isFiltersToIncludeConfigVisible = signal(false);

  /** Summarizes "Filters to Include"'s checkbox state as a sentence, kept visible by default with the checkboxes
   * themselves tucked behind the config icon — it re-evaluates whenever a checkbox changes, and disappears
   * entirely once nothing is checked (there's nothing left to summarize). */
  public readonly filtersToIncludeSentence = computed(() => {
    const includedFields: ('date' | 'lineId' | 'shiftId')[] = [
      ...(this.includeDateSignal() ? (['date'] as const) : []),
      ...(this.includeLineSignal() ? (['lineId'] as const) : []),
      ...(this.includeShiftSignal() ? (['shiftId'] as const) : []),
    ];

    if (!includedFields.length) {
      return null;
    }

    const fields = includedFields.map((field) => this.translate.instant(`advanceFilter.field.${field}`)).join(', ');

    return this.translate.instant('advanceFilter.shiftDialog.filtersToIncludeSentence', { fields });
  });

  constructor() {
    this.lineResource?.trigger.set(true);
    this.shiftResource?.trigger.set(true);

    this.shiftFilterForm.controls.mode.valueChanges
      .pipe(startWith(this.shiftFilterForm.controls.mode.value), takeUntilDestroyed())
      .subscribe((mode) => {
        this.setControlEnabledState(
          this.shiftFilterForm.controls.lineId,
          mode === 'currentShift' || mode === 'previousShift',
        );
        this.setControlEnabledState(this.shiftFilterForm.controls.shiftIds, mode === 'other');
      });
  }

  public ngOnInit(): void {
    this.allowLineOptions.set(true);
  }

  /** Editing the merged current/previous-shift chip seeds the Line dropdown with the couple's own shared value —
   * that line selection belongs to the pair being edited, not to whatever separate `lineId` filter may or may not
   * also be on the dashboard, so it must not be re-derived from `unappliedFilters` in this case.
   *
   * Otherwise, a single line filter already on the dashboard (`$in` with exactly one id — the only unambiguous
   * case, matching how the backend migration resolves a sibling lineId) seeds the Line dropdown immediately,
   * before its options have even loaded — `hasDelayedDefaultValue` on `lineDropdownSettings` swaps the placeholder
   * `'...'` name for the real one once `lineDropdownOptions()` arrives. */
  private getInitialLineIdValue(): IDropdownOption[] {
    if (this.dialogData) {
      return [{ id: this.dialogData.lineId, name: '...' }];
    }

    const lineFilter = this.advancedFilterService.manager.unappliedFilters().find(({ field }) => field === 'lineId') as
      | IMultiselectApiDropdownFilterSave
      | undefined;
    const value = lineFilter?.value;

    return Array.isArray(value) && value.length === 1 ? [{ id: value[0], name: '...' }] : [];
  }

  public onFormDialogSubmit(): void {
    this.shiftFilterForm.markAllAsTouched();

    if (!this.shiftFilterForm.valid || this.isNoFilterToAddSelected()) {
      return;
    }

    const { controls } = this.shiftFilterForm;
    const mode = controls.mode.value;

    if (mode === 'other') {
      this.matDialogRef.close([
        {
          field: 'shiftId',
          operator: ESqlOperators.$in,
          value: controls.shiftIds.value.map(({ id }) => id as number),
        } satisfies IMultiselectApiDropdownFilterSave,
      ]);

      return;
    }

    const operator = mode === 'currentShift' ? ESqlOperators.$currentShift : ESqlOperators.$previousShift;
    const lineId = controls.lineId.value[0].id as number;
    const filters: TFilterSave[] = [];

    if (controls.includeDate.value) {
      filters.push({ field: 'date', operator, value: lineId } satisfies IShiftPhraseFilterSave);
    }

    if (controls.includeShift.value) {
      filters.push({ field: 'shiftId', operator, value: lineId } satisfies IShiftPhraseFilterSave);
    }

    if (controls.includeLine.value) {
      filters.push({
        field: 'lineId',
        operator: ESqlOperators.$in,
        value: [lineId],
      } satisfies IMultiselectApiDropdownFilterSave);
    }

    this.matDialogRef.close(filters);
  }

  public toggleFiltersToIncludeConfig(): void {
    this.isFiltersToIncludeConfigVisible.update((isVisible) => !isVisible);
  }

  private setControlEnabledState(control: FormControl<unknown>, isEnabled: boolean): void {
    if (isEnabled === control.enabled) {
      return;
    }

    if (isEnabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }
}
